import { Dayjs } from 'dayjs';
import { v4 as uuid } from 'uuid';

import { isString } from '@summit/core';

import { SummitFormattedText } from '@summit/models';
import { EboNameObjectTO } from '../base-classes/ebo-name-object-to';
import { CalendarEvent } from '../calendar/calendar-event';
import { Employee } from '../employee/employee';
import { UserExperience } from '../shared/user-experience';
import { WorkFlowStep } from '../work-flow/work-flow-step.enum';
import { EProcess } from './e-process';
import { EProcessStatus } from './e-process-status.enum';
import { EProcessType } from './e-process-type.enum';
import { ETaskAssignee } from './e-task-assignee';

export class ETask extends EboNameObjectTO {
  eProcessId: string;
  eProcess: EProcess;
  path: number;
  cycle: number;
  completerId: string;
  completer: Employee;
  status: EProcessStatus;
  currentStep: WorkFlowStep;
  comment: string;
  assignees: ETaskAssignee[];

  constructor(jsonData?: Partial<ETask>) {
    super(jsonData);
    if (jsonData) {
      if (isString(this.status)) this.status = parseInt(this.status);
      if (jsonData.eProcess) this.eProcess = new EProcess(jsonData.eProcess);
      if (jsonData.completer) this.completer = new Employee(jsonData.completer);
      if (jsonData.assignees) {
        this.assignees = jsonData.assignees.map((assignee) => new ETaskAssignee(assignee));
      }
    }
    this.eProcess ??= new EProcess();
    this.eProcessId ??= this.eProcess.id;
    this.status ??= EProcessStatus.Pending;
    //console.log('ETask.constructor', this);
  }

  get description(): string {
    return this.eProcess?.description;
  }
  set description(value: string) {
    this.eProcess ??= new EProcess();
    this.eProcess.description = value;
  }

  get calendarEvents(): CalendarEvent[] {
    //console.log('ETask.calendarEvents', this.eProcess?.calendarEvents);
    return this.eProcess?.calendarEvents;
  }
  set calendarEvents(value: CalendarEvent[]) {
    this.eProcess ??= new EProcess();
    this.eProcess.calendarEvents = value;
  }
  get createdOn(): Dayjs {
    return this.eProcess?.createdOn;
  }

  get createdBy(): Employee {
    return this.eProcess?.createdBy;
  }
  get processType(): EProcessType {
    return this.eProcess?.type;
  }
  set processType(value: EProcessType) {
    this.eProcess ??= new EProcess();
    this.eProcess.type = value;
  }
  get dueDateTime(): Dayjs {
    return this.eProcess?.dueDateTime;
  }
  get employeeID(): string {
    return this.eProcess?.employeeID;
  }
  set employeeID(value: string) {
    this.eProcess ??= new EProcess();
    this.eProcess.employeeID = value;
  }
  get employee(): Employee {
    return this.eProcess?.employee;
  }
  set employee(value: Employee) {
    this.eProcess ??= new EProcess();
    this.eProcess.employee = value;
  }

  get subject(): string {
    return this.eProcess?.name ?? this.name;
  }
  set subject(value: string) {
    //nop
  }

  get assigneeSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    this.assignees.forEach((assignee) => {
      response.AddLine(assignee.toSummaryText());
    });
    return response;
  }
  set assigneeSummary(value: SummitFormattedText) {
    //nop
  }

  toShortString(): string {
    return `${this.name} (${this.status})`;
  }

  toNewTemplate(): ETask {
    const response = new ETask(this);
    response.id = uuid();
    response.eProcessId = uuid();
    response.eProcess.id = response.eProcessId;
    response.eProcess.status = EProcessStatus.Pending;
    response.status = EProcessStatus.Pending;
    response.eProcess.createdById = undefined;
    response.eProcess.createdBy = undefined;
    response.eProcess.createdOn = undefined;
    response.completerId = undefined;
    response.completer = undefined;
    response.modifiedBy = undefined;
    response.modifiedById = undefined;
    response.modifiedOn = undefined;
    response.modifiedViaId = undefined;
    if (response.calendarEvents?.length > 0) {
      response.calendarEvents.forEach((event) => {
        event.id = uuid();
        event.requestId = response.eProcessId;
      });
    }
    return response;
  }

  isApprovable(ux: UserExperience): boolean {
    if (this.status !== EProcessStatus.Pending) return false;
    if (ux.isAdmin()) return true;
    const approvableTask = this.assignees.find((assignee) => assignee.isApprover(ux));
    return approvableTask !== undefined;
  }
}
